import logo from '@/assets/logo.png';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { buildOrgUrl, getOrgSlugFromHostname } from '@/core/domain';
import { setToken } from '@/core/token';
import { useMe } from '@/hooks/useMe';
import { cn } from '@/lib/utils';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { useFormik } from 'formik';
import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

export const VerifySignupForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const navigate = useNavigate();
  const me = useMe();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const secret = searchParams.get('secret') || '';
  const email = searchParams.get('email') || '';

  const formik = useFormik({
    initialValues: {
      code: '',
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.code) {
        errors.code = 'Please enter the verification code';
      } else if (values.code.length !== 6) {
        errors.code = 'Code must be 6 digits';
      }
      return errors;
    },
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);

      const toastId = toast.loading('Verifying code...', {
        description: 'Please wait while we verify your code',
      });

      try {
        const captchaToken = await executeRecaptcha?.('verify_signup');

        const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/verify-signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            secret,
            code: values.code,
            captchaToken,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          formik.setFieldError('code', data.error || 'Invalid code');
          throw new Error(data.error || 'Invalid code');
        }

        if (data.success && data.token) {
          setToken(data.token);
          me.loadMe();
          toast.success('Email verified!', {
            description: 'Your account has been verified successfully',
            id: toastId,
          });
          const slugs: string[] | undefined = data.organizationSlugs;
          const currentSlug = getOrgSlugFromHostname();
          if (currentSlug && slugs?.includes(currentSlug)) {
            navigate('/');
          } else if (slugs && slugs.length > 0) {
            window.location.href = buildOrgUrl(slugs[0]);
          } else {
            navigate('/auth/onboarding/org');
          }
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Verification failed';
        setError(errorMessage);
        toast.error('Verification failed', {
          description: errorMessage,
          id: toastId,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleResendCode = async () => {
    setIsResending(true);
    const toastId = toast.loading('Resending code...', {
      description: 'Please wait while we send a new code',
    });

    try {
      const captchaToken = await executeRecaptcha?.('resend_signup');

      const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/resend-signup-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          captchaToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend code');
      }

      if (data.success) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('secret', data.secret);
        setSearchParams(newParams);
        formik.resetForm();
        setError(null);
        toast.success('New code sent!', {
          description: 'Check your email for the new verification code',
          id: toastId,
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resend code';
      toast.error('Failed to resend code', {
        description: errorMessage,
        id: toastId,
      });
    } finally {
      setIsResending(false);
    }
  };

  if (!secret || !email) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <Card className='overflow-hidden p-0'>
          <CardContent className='p-6 md:p-8 bg-white dark:bg-stone-900'>
            <div className='flex flex-col items-center text-center gap-4'>
              <img src={logo} alt='Logo' className='size-16' />
              <h1 className='text-2xl font-bold'>Invalid Link</h1>
              <p className='text-muted-foreground'>This verification link is invalid or has expired.</p>
              <Link to='/auth/signup'>
                <Button>Back to Sign Up</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='grid p-0 md:grid-cols-2 bg-white dark:bg-stone-900'>
          <form className='p-6 md:p-8' onSubmit={formik.handleSubmit}>
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col items-center text-center'>
                <img src={logo} alt='Logo' className='size-16 mb-6' />
                <h1 className='text-2xl font-bold'>Verify your email</h1>
                <p className='text-muted-foreground text-balance'>
                  We sent a 6-digit code to <strong>{email}</strong>
                </p>
                {error && (
                  <div className='mt-4 p-3 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-md'>
                    <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>
                  </div>
                )}
              </div>
              <div className='grid gap-2'>
                <Label>Verification Code</Label>
                <InputOTP
                  maxLength={6}
                  pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                  value={formik.values.code}
                  onChange={(value) => {
                    formik.setFieldValue('code', value);
                    if (formik.errors.code) {
                      formik.setFieldError('code', '');
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') formik.handleSubmit(); }}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
                {formik.touched.code && formik.errors.code && (
                  <p className='text-sm text-destructive'>{formik.errors.code}</p>
                )}
              </div>
              <Button type='submit' className='w-full' disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify Email'}
              </Button>
              <div className='flex flex-col gap-2 text-center'>
                <Button
                  type='button'
                  variant='link'
                  onClick={handleResendCode}
                  disabled={isResending || isLoading}>
                  {isResending ? 'Resending...' : "Didn't receive a code? Resend"}
                </Button>
                <Link to='/auth/signup' className='text-sm text-muted-foreground underline underline-offset-4 hover:text-primary'>
                  Back to Sign Up
                </Link>
              </div>
            </div>
          </form>
          <div className='bg-muted relative hidden md:block'>
            <img
              src='/placeholder.svg'
              alt='Image'
              className='absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale'
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
